/**
 * The first screen after signing in.
 *
 * It answers the three questions a developer opens a dashboard to ask: is
 * anything broken, is anything happening right now, and how long can I keep
 * paying for it. Everything else is a click away.
 */

import { Link } from 'react-router-dom'
import {
  IconBilling,
  IconDatabase,
  IconPlus,
  IconRocket,
  IconServices,
} from '../components/icons'
import RequestError from '../components/RequestError'
import Metric from '../components/Metric'
import StatusBadge, { isInFlight } from '../components/StatusBadge'
import { CardSkeleton, EmptyState, ListSkeleton } from '../components/ui'
import { api } from '../lib/api'
import { useLiveRefresh } from '../lib/events'
import { money, daysUntil, deployTitle, relativeTime, shortSha } from '../lib/format'
import type { Activity, Wallet } from '../lib/types'
import { useApi } from '../lib/useApi'
import { useAuth } from '../lib/useAuth'

function greeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

/** Runway in plain words — "3 days left" is actionable, "71.4" is not. */
/** What the wallet tile says under the balance: the next thing that will be
 *  taken out of it, or that nothing will be. */
function nextCharge(wallet: Wallet): string {
  const trial = daysUntil(wallet.trial_ends_at)
  if (trial !== null && trial > 0) {
    return `free for ${trial} more ${trial === 1 ? 'day' : 'days'}`
  }
  const days = daysUntil(wallet.next_charge_at)
  if (days === null) return 'nothing billing yet'
  if (days <= 0) return `${money(wallet.monthly_burn, wallet.currency)} due now`
  return `${money(wallet.monthly_burn, wallet.currency)} in ${days} ${days === 1 ? 'day' : 'days'}`
}

export default function Overview() {
  const { user } = useAuth()

  const services = useApi(() => api.services(), [], 120_000)
  const activity = useApi(() => api.activity(12), [], 120_000)
  const databases = useApi(() => api.databases(), [], 120_000)
  const wallet = useApi(() => api.wallet(), [], 120_000)

  useLiveRefresh(() => {
    services.reload()
    activity.reload()
    databases.reload()
  }, () => true)

  const list = services.data ?? []
  const dbs = databases.data ?? []
  const loading = (services.loading && !services.data) || (databases.loading && !databases.data)
  const healthUnknown = !!services.error || !!databases.error
  const queries = [{ name: 'Components', ...services }, { name: 'Data & storage', ...databases }, { name: 'Wallet', ...wallet }]

  const live = list.filter((s) => s.status === 'live').length
  const building = list.filter((s) => s.status === 'building').length
  const inFlight = (activity.data ?? []).filter((d) => isInFlight(d.status))
  const attention = [
    ...list.filter((s) => s.status === 'failed' || s.status === 'suspended'),
    ...dbs.filter((d) => d.status === 'failed' || d.status === 'suspended'),
  ]
  // Repositories whose pushes will not deploy, one entry per repository. A
  // ticked "deploy on push" box over a hook GitHub was never told to send is
  // the quietest failure this platform has had, and this is where it is
  // said out loud: the first screen, before anything else.
  const silentRepos = new Map<string, { serviceId: string; error: string }>()
  for (const s of list) {
    if (s.auto_deploy && s.webhook && s.webhook.status !== 'active') {
      if (!silentRepos.has(s.repo_full_name)) {
        silentRepos.set(s.repo_full_name, { serviceId: s.id, error: s.webhook.error ?? '' })
      }
    }
  }

  if (loading) {
    return (
      <>
        <header className="page-head">
          <div className="titles">
            <h1>{greeting()}</h1>
          </div>
        </header>
        <div className="grid-3">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
        <ListSkeleton rows={4} />
      </>
    )
  }

  if (!healthUnknown && list.length === 0 && dbs.length === 0) {
    return (
      <>
        <header className="page-head">
          <div className="titles">
            <h1>{greeting()}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</h1>
            <p className="subtitle">Let’s get something running in Addis Ababa.</p>
          </div>
        </header>
        <EmptyState
          icon={<IconRocket className="empty-art" size={46} />}
          title="Deploy your first application"
          action={
            <Link className="btn primary" to="/services/new">
              <IconPlus className="" size={16} />
              New application
            </Link>
          }
        >
          Connect a GitHub repository and TalariCloud works out how to build it —
          no Dockerfile, no YAML. Your first service is free.
        </EmptyState>
      </>
    )
  }

  return (
    <>
      <header className="page-head">
        <div className="titles">
          <h1>{greeting()}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</h1>
          <p className="subtitle">
            {healthUnknown ? 'Health could not be fully checked. Review the unavailable sections below.' : attention.length === 0
              ? building > 0
                ? `${building} deploy${building === 1 ? '' : 's'} in progress. Other resource states are shown below.`
                : 'Your workspace at a glance.'
              : `${attention.length} thing${attention.length === 1 ? '' : 's'} need${
                  attention.length === 1 ? 's' : ''
                } your attention.`}
          </p>
        </div>
        <Link className="btn primary" to="/services/new">
          <IconPlus className="" size={16} />
          New application
        </Link>
      </header>

      {queries.filter(query => query.error).map(query => <RequestError key={query.name} title={`${query.name} unavailable`} message={query.error!} retry={query.reload} />)}

      {silentRepos.size > 0 && (
        <div className="stack" style={{ gap: 8 }}>
          {[...silentRepos.entries()].map(([repo, { serviceId, error }]) => (
            <div className="alert warn" role="status" key={repo}>
              <div className="grow">
                <strong>
                  Pushes to <span className="mono">{repo}</span> will not deploy.
                </strong>{' '}
                {error}
              </div>
              <Link className="btn ghost" to={`/services/${serviceId}/settings`}>
                Fix
              </Link>
            </div>
          ))}
        </div>
      )}

      <div className="grid-3">
        <Metric
          icon={<IconServices className="" size={17} />}
          label="Services"
          value={services.error ? '—' : `${live}/${list.length}`}
          hint={services.error ? 'Status unavailable' : live === list.length ? 'all live' : `${list.length - live} not live`}
          tone={attention.some((item) => !('engine' in item)) ? 'bad' : undefined}
          to="/services"
        />
        <Metric
          icon={<IconDatabase className="" size={17} />}
          label="Data & Storage"
          value={databases.error ? '—' : String(dbs.filter((d) => d.status === 'available').length)}
          hint={databases.error ? 'Status unavailable' : dbs.length === 0 ? 'none yet' : `of ${dbs.length} provisioned`}
          to="/databases"
        />
        <Metric
          icon={<IconBilling className="" size={17} />}
          label="Wallet"
          value={wallet.data ? money(wallet.data.balance, wallet.data.currency) : '—'}
          hint={wallet.error ? 'Balance unavailable' : wallet.data ? nextCharge(wallet.data) : 'Loading balance…'}
          tone={wallet.data?.low_balance ? 'bad' : undefined}
          emphasis={!wallet.data?.low_balance}
          to="/billing"
        />
      </div>

      {attention.length > 0 && (
        <section className="stack">
          <h2 className="section-title">Needs attention</h2>
          <div className="list">
            {attention.map((item) => (
              <Link
                className="list-item"
                key={item.id}
                to={
                  'engine' in item ? `/databases/${item.id}` : `/services/${item.id}`
                }
              >
                {'engine' in item ? (
                  <IconDatabase className="" size={15} />
                ) : (
                  <IconRocket className="" size={15} />
                )}
                <span className="grow truncate">{item.name}</span>
                <StatusBadge status={item.status} />
              </Link>
            ))}
          </div>
        </section>
      )}

      {inFlight.length > 0 && (
        <section className="stack">
          <h2 className="section-title">In progress</h2>
          <div className="list">
            {inFlight.map((deploy) => (
              <ActivityRow key={deploy.id} deploy={deploy} showService />
            ))}
          </div>
        </section>
      )}

      <section className="stack">
        <h2 className="section-title">Recent deploys</h2>
        {activity.error ? <RequestError title="Activity unavailable" message={activity.error} retry={activity.reload} /> : !activity.data ? (
          <ListSkeleton rows={3} />
        ) : activity.data.length === 0 ? (
          <div className="list">
            <div className="list-item muted tiny">
              Nothing deployed yet. Push to a tracked branch, or deploy by hand
              from a service.
            </div>
          </div>
        ) : (
          <div className="list">
            {activity.data
              .filter((deploy) => !isInFlight(deploy.status))
              .slice(0, 8)
              .map((deploy) => (
                <ActivityRow key={deploy.id} deploy={deploy} showService />
              ))}
          </div>
        )}
      </section>
    </>
  )
}

function ActivityRow({
  deploy,
  showService = false,
}: {
  deploy: Activity
  showService?: boolean
}) {
  return (
    <Link className="list-item" to={`/deploys/${deploy.id}`}>
      <StatusBadge status={deploy.status} />
      <div className="grow" style={{ minWidth: 0 }}>
        <div className="truncate">
          {showService && <strong>{deploy.service_name} · </strong>}
          {deployTitle(deploy)}
        </div>
        <div className="tiny faint">
          <span className="mono">{shortSha(deploy.commit_sha)}</span>
          {` · ${deploy.trigger} · ${relativeTime(deploy.created_at)}`}
        </div>
      </div>
    </Link>
  )
}

